import {
  PaymentSystem,
  Product as ProductDetails,
  Currency,
  ModeCardForm,
} from "@web-solutions/react-billing/constants";
import {
  RemoteConfig as ManageRemoteConfig,
  INITIAL_CONFIG as MANAGE_INITIAL_CONFIG,
} from "@web-solutions/manage-subscription/src/manage/constants/remote-config";
import { type LString } from "@web-solutions/module-localization";
import { LinkTarget } from "@web-solutions/module-attribution";
import { type SpecialOfferMode } from "@web-solutions/core/interfaces/specialOffer";
import type { Theme } from "@web-solutions/core/interfaces/theme";
import type { NoFundsOffer } from "@web-solutions/core/interfaces/remote-config";
import type { ProductAttachment } from "@web-solutions/core/interfaces/products";

import { ImageSource } from "core/interfaces/images";

import { REDIRECT_TO_CHROME } from "core/utils/redirect-to-chrome";

import {
  PriceAfterTrialType,
  ButtonPlacementType,
  ButtonBehavior,
  CardFormLayout,
  ButtonConfig,
} from "core/payment/constants";

import {
  INITIAL_CONFIG_UPSALES,
  RemoteConfigUpsales,
} from "core/upsale/constants/remote-config";

import {
  ModeApplePay,
  ModeGooglePay,
  ModePayPal,
  InertialApplePay,
  ErrorPopupType,
  ModeMercadoPago,
} from "./billing";
import { Placements } from 'core/payment/base/components/payment-description';

export interface PolicyLinks {
  termsOfUse: string;
  privacy: string;
  moneyBack: string;
  billingTerms: string;
  address?: string;
  phone?: string;
  legalEntity?: string;
  descriptor?: string;
}

const POLICY_LINKS: PolicyLinks = {
  termsOfUse: "/terms-of-use",
  privacy: "/privacy-policy",
  moneyBack: "/money-back-policy",
  billingTerms: "/billing-terms",
};

const BTNS_CONFIGS: {
  [key in ButtonPlacementType]: ButtonConfig;
} = {
  [ButtonPlacementType.PAYMENT]: {
    title: "",
    uppercaseTitle: false,
  },
  [ButtonPlacementType.PLANS]: {
    behavior: ButtonBehavior.MODAL,
    title: "",
    uppercaseTitle: false,
    enabledFlareAnimation: true,
  },
  [ButtonPlacementType.FIXED]: {
    behavior: ButtonBehavior.SCROLL,
    title: "",
    uppercaseTitle: false,
    enabledFlareAnimation: true,
  },
};

export { LString };

export interface CurrencyConfig {
  notActivePrice: string;
  activePrice: string;
  price: number;
  discount: string;
}

export interface BaseOfferProductTypes<T> {
  title: LString;
  subTitle: string;
  notActivePrice: string;
  activePrice: string;
  discount: LString;
  price: number;
  currency: Currency;
  id: T;
  EUR?: CurrencyConfig;
  CAD?: CurrencyConfig;
  AUD?: CurrencyConfig;
  USD?: CurrencyConfig;
}


interface PlanItem {
  title: string;
  subtitle: string;
  desc: string;
  infoTitle: string;
  infoSubtitle: string;
  infoDesc: string;
}

export type ProductConfig = {
  id?: string;
  trigger?: string;
  paypalPlanId?: string;
  recurlyId: string;
  solidgateId?: string;
  truegateId?: string;
  paddleId?: string;
  title?: LocalizedString;
  name?: LString;
  subTitle?: LocalizedString;
  descriptionText?: LString;
  badgeTitle?: LString;
  badge?: string;
  badgeCentered?: boolean;
  discount: number;
  trialDiscount?: number;
  default?: boolean;
  textLineThrough?: string | LString;
  accentTitle?: LString;
  accentSubTitle?: LString;
  accentTextLineThrough?: LString;
  aboutTrialText?: LString;
  afterTrialText?: LString;
  showAfterTrialPrice?: boolean;
  sold?: { left: number; total: number; hiddenSubTitles?: boolean };
  promocode?: {
    discount: string;
    discountPrice: string;
  };
  smallBadges?: string[];
  trialsLeftBadge?: boolean;
  buttonTitle?: LString;
  attachment?: ProductAttachment;
  perWeekTitle?: string;
  priceStartTrial?: {
    price: LocalizedString;
    billingPeriod: LocalizedString;
    billedAmount: string;
    totalAmount: string;
    buttonLabel: string;
    plan_end_days: string;
  };
  perWeekText?: LocalizedString;
  paid_status: "free" | "promo" | "paid" | "trail";
  plan_end_days?: string;
  productTitle?: LocalizedString;
  trialText?: LocalizedString;
  isDiscount?: boolean;
  productCode?: string;
  messageTemplate?: string;
  oldTrialPriceAmount?: number;
  oldTrialPriceText?: string;
  oldAmount?: number | string;
  perDayOldPriceAmount?: number;
  savedAmount?: number | string;
  savedTrialPriceText?: string;
  perDayPriceAmount?: number;
  savedTrialPriceAmount?: number;
  paymentDescription: {
    plan?: string;
    period?: string;
  };

  planItem: PlanItem;

  ladder?: {
    recurlyId: string;
    amount: string;
  }[];
};

type PaymentDescriptionKeys =
  | 'hintTitle'
  | 'hint'
  | 'hintBottom'
  | 'security'
  | 'privacyTitle'
  | 'privacyCheckboxTitles'
  | 'privacy';

type PaymentDescriptionPlacementKeys = Exclude<PaymentDescriptionKeys, 'privacyCheckboxTitles'>;

type PaymentDescription = {
  [key in Exclude<PaymentDescriptionKeys, 'privacyCheckboxTitles'>]: string;
} & {
  privacyCheckboxTitles?: string[];
};

type PaymentDescriptionPlacements = {
  [key in Placements]: PaymentDescriptionPlacementKeys[];
};

type AdditionalFields = {
  email?: {
    label: string;
    type: string;
    isRequired: boolean;
  };
  first_name?: {
    label: string;
    placeholder?: string;
    type: string;
  };
  last_name?: {
    label: string;
    placeholder?: string;
    type: string;
  };
  cardholder?: boolean;
  zip?: boolean;
};

export enum InjectedPaymentMethod {
  APPLEPAY = "applepay",
  PAYPAL = "paypal",
  CARD = "card",
  GOOGLEPAY = "googlepay",
  APPLEPAY_PAYPAL = "applepay_paypal",
  GOOGLEPAY_PAYPAL = "googlepay_paypal",
  APPLEPAY_GOOGLEPAY = "applepay_googlepay",
}

export enum PaymentModalType {
  BASE = "base",
  BOTTOM_INJECTED = "bottom_injected",
  TOP_INJECTED = "top_injected",
  SIMPLE = "simple",
  SIMPLE_TITLE = "simple_title",
}

export enum PlanType {
  DEFAULT = "default",
  SINGLE = "single",
  HORIZONTAL = "horizontal",
  PDF = "pdf",
  TICKET = "ticket",
  MODAL = "modal",
}

interface Quiz {
  magic: {
    subTitle: LocalizedString;
  };
  answersTitle: LocalizedString;
  buttonTextResults: LocalizedString;
}

export interface LocalizedString {
  en: string;
  es: string;
  fr: string;
  pt: string;
  de: string;
  tr: string;
  it: string;
  cs: string;
  ro: string;
  pl: string;
  id: string;
}
export interface LocalizedArray {
  en: string[];
  es: string[];
  fr: string[];
  pt: string[];
  de: string[];
  tr: string[];
  it: string[];
  cs: string[];
  ro: string[];
  pl: string[];
  id: string[];
}

interface Email {
  title: LocalizedString;
  button: LocalizedString;
  placeholder: LocalizedString;
  notificationText: LocalizedString;
}

export type PriceTypes = "today" | "week" | "day";
export type PlanTypes = 'basic' | 'standart' | 'premium';

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export interface RemoteConfig<FlowStep = string, ChatFlow = string>
  extends ManageRemoteConfig,
    RemoteConfigUpsales {
  theme: Theme;

  oneClickApplePayGooglePayMode: "1click" | "native" | "modal" | "native+modal";
  isOneClickFootnote: boolean;
  isDownloadPdfInBrowser: boolean;
  isCvvTooltip: boolean;
  displayVAT: boolean;
  usdConversionRates: Record<Currency, number>;
  configVAT: {
    withGeo: boolean;
    view: "simple" | "table";
  };
  linkTarget: LinkTarget;
  planType: PlanType;
  plansFeatures: string[];
  paymentDescriptionType: "old" | "new" | "icons";
  showTimerInPlanBadge: boolean;
  paymentModalEmailEnabled?: boolean;
  paymentProject: string;
  paymentSystem: PaymentSystem;
  paymentErrorRetries: {
    enabledFunds: boolean;
    enabled3ds: boolean;
    error3dsCodes: string[];
  };
  paymentModalHeader: {
    title?: LString;
    subtitle?: LString;
  };
  simpleTitleModal: {
    type: "default" | "short";
    titleEnabled: boolean;
    text: LString;
    fontSize?: number;
    withTotalPriceUnder?: boolean;
  };
  paymentSystemReserve: PaymentSystem | "";
  products: ProductConfig[];
  productsDetails: Array<
    Omit<ProductDetails, "period"> & { period: "ONETIME" }
  >;
  additionalFields: AdditionalFields;
  policyLinks: PolicyLinks;
  address: LocalizedString;
  buttonsConfigs: typeof BTNS_CONFIGS;
  priceAfterTrialType: PriceAfterTrialType;
  productsTitle: LocalizedString;
  showAfterTrialPrice: boolean;
  showPricesBlock: boolean;
  showPriceDiscount: boolean;
  openPaymentModalOnProductClick: boolean;
  isShowSafeCheckout: boolean;
  discountTime: number;
  modePayPal: ModePayPal;
  modeApplePay: ModeApplePay;
  inertialApplePay: InertialApplePay;
  modeGooglePay: ModeGooglePay;
  modeMercadoPago: ModeMercadoPago;
  cardFormCollapsed: boolean;
  cardFormLayout: CardFormLayout;
  showPaymentSafeAtPlans: boolean;
  showTermsAtPlans: boolean;
  totalPriceType: PriceTypes;
  isInjectedPaymentMethodOnPage: boolean;
  redirectToSuccessOnDecline: boolean;
  paymentModalType: PaymentModalType;
  trialInfoBlock: {
    show: boolean;
    periodMode: "day" | "week";
  };
  injectedPaymentMethods: InjectedPaymentMethod[];
  defaultInjectedPaymentMethod: InjectedPaymentMethod;
  quizPaymentModalFullMode: boolean;
  isPaymentModalShowAfterQuiz: boolean;
  errorPopup: {
    enabled: boolean;
    buttonTitle: string;
    type: ErrorPopupType;
    isDetailedErrors: boolean;
  };
  withOnlyEngFootnote: boolean;
  flow: Array<FlowStep | FlowStep[]>;
  consentCookieModal: {
    enabled: boolean;
    type: "long" | "short";
    placement: FlowStep;
  };
  isTrialPeriodReminder: boolean;
  trialsLeft: {
    badgeButtonEnabled: boolean;
    textEnabled: boolean;
    time: number;
  };
  promocodeOffer: {
    enabled: boolean;
    showPaymentModalDiscount: boolean;
    switchDefaultValue: boolean;
    products: ProductConfig[];
  };
  specialOffer: {
    // без mode switch в renderSpecialOffer не рендерит ничего — это и есть «выключено».
    // Дефолта здесь быть не должно: иначе неполный конфиг мутатора включает модалку сам.
    mode?: SpecialOfferMode;
    enabled: boolean;
    time: number;
    prevDiscount: number;
    discount: number;
    showAfterCloseAmount: number;
    products: ProductConfig[];
    scrollToTopAfterTrigger?: boolean;
    title?: LString;
    soInfoOfferText?: LString;
    guaranteedText: LString;
    guaranteedLink: LString;
    button: LocalizedString;
    guaranteedSaveText: LocalizedString;
    // читается в layouts/plans через state.remoteConfig.specialOffer
    highlightWords: string[];
  };
  noFundsOffer: NoFundsOffer;
  subscriptionPageType: 'usual' | 'cerebrum';
  redirectToChrome: REDIRECT_TO_CHROME;
  isSendingTerminateLinks: boolean;
  isSendingTrialEnd: boolean;
  isTrialPriceSelection: boolean;
  chatFlow: {
    flow: Array<ChatFlow>;
    welcomeAnswerType: "single" | "multi" | "none";
    symbolsPerSecond: number;
    palmEnabled: boolean;
    redirectToSummary: boolean;
    nameStepEnabled: boolean;
    buttonText: LString;
  };
  modeCardForm: ModeCardForm;
  emailReportlLinkMode: "download" | "open" | "download+open";
  oneClickPaymentMode: '1click' | 'native' | 'modal' | 'native+modal';
  paymentDescription: PaymentDescription;
  paymentDescriptionPlacements: PaymentDescriptionPlacements;
  core: {
    termsInfo: {
      contact_us: LocalizedString;
      privacy_policy: LocalizedString;
      terms: LocalizedString;
      billing_terms: LocalizedString;
      money_back: LocalizedString;
    };
    quiz: Quiz;
    email: Email;
    summary: {
      plan: LocalizedString;
      image: ImageSource;
    };
    success: {
      title: {
        text: LocalizedString;
        highlightedText: Array<string>;
      };
      description: {
        text: LocalizedString;
        highlightedText: Array<string>;
      };
      footnote: LocalizedString;
      app: string;
      btn: LocalizedString;
      steps: {
        first: LocalizedString;
        second: LocalizedString;
        third: LocalizedString;
        fourth: LocalizedString;
      };
    };
  };
  paymentPopup: {
    total: {
      today: LocalizedString;
      day: LocalizedString;
      week: LocalizedString;
      total: LocalizedString;
      totalItem: LocalizedString;
    };
    selectedTitles: LocalizedString;
    trial: LocalizedString;
    editEmail: {
      title: LocalizedString;
      placeholder: LocalizedString;
      doubleCheck: LocalizedString;
      button: LocalizedString;
    };
    or: LocalizedString;
    payWithCard: LocalizedString;
    cardElementRecurly: {
      cardNumber: LocalizedString;
      mm: LocalizedString;
      yy: LocalizedString;
      firstName: LocalizedString;
      lastName: LocalizedString;
    };
    discountSave: LocalizedString;
    showPrivacy: boolean;
  };
  localizationEnabled: boolean;
  transitionVisibilityGender: "all" | "male-only" | "female-only";
  isShowedSkipButton: boolean;
  showGeneratedPhotoOnPaywall: boolean;
  upsaleOffer: {
    product: string;
    upsaleName: string;
    recurlyId: string;
    discount: string;
    textLineThrough: string;
    priceAmount: string;
  };
  showSpecialOffer: boolean;
  showFaceScan: boolean;
  fbPixelId: number;
  routeGroups: string[][];
  upsaleFirst: {};
  skipPosition: string;
  upsaleWithoutTrial: {};
  showBeforeAfterOnPaywall: boolean;
  showPlanDescription: boolean;
  prelandText: {
    title: string;
    subTitle: string;
  };
  showImage: boolean;
  showMealPlanPaywall: boolean;
  showEmailMeals: boolean;
  showEmailReviews: boolean;
  showTwoPhones: boolean;
  addBlurDay1: boolean;
  dynamicPages: [];
  showEmailPopup: boolean;
  showGiftPopup: boolean;
  // Deferred-email checkout: the user pays without giving an email, which is then
  // collected on the dedicated `email-after-payment` page. Both the pre-payment
  // customer and the way the email is tied to the purchase depend on the payment
  // system:
  //  - Stripe:   an anonymous customer (empty POST /stripe/customers) on paywall
  //              open; that response also yields the guest Bearer token, which
  //              POST /auth/email/attach uses to identify the guest.
  //  - Truegate: the truegate billing backend keys the customer by email, so it
  //              is created with a synthetic per-user `anon-<uuid>@voery.app`
  //              address (see helpers/anonEmail.ts) that never leaves that
  //              backend. There is no guest token, so POST /truegate/claim ties
  //              the email to the purchase by its transaction id(s) instead.
  // Only takes effect for those two payment systems.
  collectEmailAfterPayment: boolean;
  // Stripe deferred-email only: when enabled, the native Apple Pay sheet requests
  // the payer's email so the email-after-payment page can be skipped when it's
  // present. NOTE: requesting it makes Apple Pay REQUIRE an email — if the user
  // has none in Wallet, the sheet forces entry. Disabled => Apple Pay never asks
  // for email and the email-after-payment page is always shown.
  requestApplePayEmail: boolean;
  upsaleNutritionBundle: {
    product: string;
    upsaleName: string;
    recurlyId: string;
    priceAmount: string;
    movePriceToBottom: boolean;
  };
  dynamicRedirectRules: [];
  usersTrust: {
    title: LocalizedString;
    reviewTitle: LocalizedString;
    reviewDescription: LocalizedString;
  };
  stats: {
    image: string;
    firstOption: LocalizedString;
    secondOption: LocalizedString;
  };
  guide: {
    title: LocalizedString;
    text: LocalizedString;
    blurText: LocalizedString;
  };
  tryFoodzee: {
    tryFoodzeeTitle: LocalizedString;
    // слой читает options[locale] как массив строк, а не как массив LocalizedString
    options: LocalizedArray;
    discount: {
      title: LocalizedString;
      subTitle: LocalizedString;
    };
  };
  features: {
    title: LocalizedString;
    subTitle: LocalizedString;
    options: Array<{
      // у voery иконки берутся по индексу из вёрстки, эмодзи в конфиге нет
      emoji?: string;
      title: LocalizedString;
      subTitle: LocalizedString;
    }>;
  };
  customersReview: Array<{
    // voery рендерит аватарки и имена из вёрстки, из конфига берёт только текст
    image?: string;
    title?: string;
    text: LocalizedString;
  }>;
  questions: Array<{
    question: LocalizedString;
    answer: LocalizedString;
  }>;
  showPlans: boolean;
  ltsPhotoExample: {
    title: LocalizedString;
    photoProgressTitle: LocalizedString;
  };
  showPhotoProgress: boolean;
  email: {
    title: LocalizedString;
    subTitle: LocalizedString;
  };
  upsaleDynamic: {
    product: string;
    upsaleName: string;
    recurlyId: string;
    priceAmount: string;
    buttonText: LocalizedString;
    title: LocalizedString;
    subTitle: LocalizedString;
    image: string;
    descriptionTitle: LocalizedString;
    descriptionText: LocalizedString;
    // страница читает whatYouGetText[locale] как массив строк, а не как массив LocalizedString
    whatYouGetText: LocalizedArray;
    bottomText: LocalizedString;
    movePriceToBottom: boolean;
    letterName: string;
    fileName: string;
    // voery: апсейл-страница берёт продукт из products.product[0], а тексты — отсюда
    products: { product: any[] };
    timerLabel: LocalizedString;
    skipText: LocalizedString;
    priceNote: LocalizedString;
  };
  timerDesign: {
    text: LocalizedString;
    buttonTitle: LocalizedString;
  };
  personalPlanPage: {
    title: LocalizedString;
    subTitle: LocalizedString;
    buttonTitle: LocalizedString;
    paymentButtonTitle: LocalizedString;
    moneyBack: {
      title: LocalizedString;
      sub1: LocalizedString;
      sub2: LocalizedString;
    };
    mealPlanTitle: LocalizedString;
    sections: string[];
  };
  giftPopup: {
    title: LocalizedString;
    buttonTitle: LocalizedString;
  };
  scrollEnabled: boolean;
  sendEthnicity: string;
  showUpsalePaymentError: boolean;
  redirectToPwa: boolean;
  newPaymentModal: boolean;
  showEmailAutocomplete: boolean;
  activeLanguages: string[];
  showGetSelfieReportPopup: boolean;
  darkBlurEnabled: boolean;
  showApplePayAnimation: boolean;
  paywall: {
    title: LocalizedString,
    optionsTitle: LocalizedString,
    options: LocalizedString[],
    buttonTitle: LocalizedString,
    paymentButtonTitle: LocalizedString,
    noPayment: LocalizedString,
    bottomText: LocalizedString,
    product: {}
  },
  changePaymentMethod: {
    title: LocalizedString,
    products: any[]
  }
}

export const INITIAL_CONFIG: RemoteConfig = {
  ...MANAGE_INITIAL_CONFIG,
  ...INITIAL_CONFIG_UPSALES,
  withOnlyEngFootnote: false,
  paymentDescriptionType: "new",
  isCvvTooltip: false,
  isOneClickFootnote: true,
  isDownloadPdfInBrowser: false,
  displayVAT: false,
  usdConversionRates: {} as Record<Currency, number>,
  planType: PlanType.DEFAULT,
  paymentErrorRetries: {
    enabledFunds: false,
    enabled3ds: false,
    error3dsCodes: [],
  },
  paymentModalHeader: {
    title: "",
    subtitle: "",
  },
  simpleTitleModal: {
    type: "default",
    titleEnabled: true,
    text: "<b>91% found</b> their happiness with highly-personalized predictions",
    fontSize: 22,
    withTotalPriceUnder: true,
  },
  plansFeatures: [],
  consentCookieModal: {
    enabled: false,
    type: "long",
    placement: "",
  },
  flow: [],
  linkTarget: LinkTarget.DEFAULT,
  showTimerInPlanBadge: false,
  paymentProject: "",
  paymentSystem: PaymentSystem.RECURLY,
  paymentSystemReserve: "",
  products: [],
  productsDetails: [],
  additionalFields: {
    email: {
      label: 'Email',
      type: 'email',
      isRequired: true,
    },
  },
  policyLinks: POLICY_LINKS,
  address: { en: "30 N. GOULD ST STE R SHERIDAN, WY 82801" } as LocalizedString,
  buttonsConfigs: BTNS_CONFIGS,
  priceAfterTrialType: PriceAfterTrialType.FULL,
  productsTitle: { en: "Choose your plan" } as LocalizedString,
  showAfterTrialPrice: true,
  showPricesBlock: true,
  showPriceDiscount: true,
  openPaymentModalOnProductClick: true,
  isShowSafeCheckout: true,
  discountTime: 120,
  modePayPal: ModePayPal.ENABLED,
  modeApplePay: ModeApplePay.ENABLED,
  inertialApplePay: InertialApplePay.DISABLED,
  modeGooglePay: ModeGooglePay.DISABLED,
  modeMercadoPago: ModeMercadoPago.DISABLED,
  cardFormCollapsed: false,
  cardFormLayout: CardFormLayout.COMPACT,
  showPaymentSafeAtPlans: false,
  showTermsAtPlans: false,
  totalPriceType: "today",
  paymentModalEmailEnabled: true,
  paymentModalType: PaymentModalType.BASE,
  trialInfoBlock: {
    show: false,
    periodMode: "day",
  },
  isInjectedPaymentMethodOnPage: false,
  redirectToSuccessOnDecline: false,
  defaultInjectedPaymentMethod: InjectedPaymentMethod.CARD,
  injectedPaymentMethods: [
    InjectedPaymentMethod.APPLEPAY,
    InjectedPaymentMethod.PAYPAL,
    InjectedPaymentMethod.CARD,
  ],
  quizPaymentModalFullMode: false,
  isPaymentModalShowAfterQuiz: false,
  errorPopup: {
    enabled: false,
    buttonTitle: "",
    type: ErrorPopupType.CARD,
    isDetailedErrors: false,
  },
  isTrialPeriodReminder: false,
  trialsLeft: {
    badgeButtonEnabled: false,
    textEnabled: false,
    time: 5000,
  },
  promocodeOffer: {
    enabled: false,
    switchDefaultValue: false,
    showPaymentModalDiscount: false,
    products: [],
  },
  specialOffer: {
    mode: "with_plans",
    enabled: false,
    time: 100,
    prevDiscount: 40,
    discount: 50,
    products: [],
    scrollToTopAfterTrigger: false,
    showAfterCloseAmount: 1,
    guaranteedLink: { en: "Learn more" },
    guaranteedText: {
      en: "In case you don’t get visible results, you can get a full refund within 30 days after purchase.",
    },
    button: { en: "Get My Report" } as LocalizedString,
    guaranteedSaveText: { en: "Guaranteed safe checkout" } as LocalizedString,
    highlightWords: [
      "SPECIAL OFFER",
      "OFERTA ESPECIAL",
      "SONDERANGEBOT",
      "OFFRE SPÉCIALE",
      "OFFERTA SPECIALE",
      "ÖZEL TEKLİF",
      "SPECIÁLNÍ NABÍDKA",
      "PENAWARAN SPESIAL",
    ],
  },
  noFundsOffer: {
    enabled: false,
    time: 100,
    discount: 50,
    products: [],
    buttonTitle: "",
  },
  subscriptionPageType: 'cerebrum',
  redirectToChrome: REDIRECT_TO_CHROME.DISABLED,
  isSendingTerminateLinks: false,
  isSendingTrialEnd: false,
  isTrialPriceSelection: false,
  chatFlow: {
    flow: [],
    welcomeAnswerType: "none",
    symbolsPerSecond: 8,
    palmEnabled: true,
    redirectToSummary: false,
    nameStepEnabled: true,
    buttonText: "",
  },
  theme: {},
  oneClickApplePayGooglePayMode: "1click",
  paymentDescription: {} as PaymentDescription,
  paymentDescriptionPlacements: {} as PaymentDescriptionPlacements,
  modeCardForm: ModeCardForm.ENABLED,
  oneClickPaymentMode: '1click',
  emailReportlLinkMode: "download",
  configVAT: {
    withGeo: true,
    view: "simple",
  },

  core: {
    termsInfo: {
      contact_us: {} as LocalizedString,
      privacy_policy: {
        en: "",
      } as LocalizedString,
      terms: {
        en: "",
      } as LocalizedString,
      billing_terms: {
        en: "",
      } as LocalizedString,
      money_back: {
        en: "",
      } as LocalizedString,
    },
    quiz: {} as Quiz,
    email: {} as Email,
    summary: {
      plan: {
        en: "Get Your Personalized\nBrain Training Plan",
      } as LocalizedString,
      image: {} as ImageSource,
    },
    success: {
      title: {
        text: {} as LocalizedString,
        highlightedText: [],
      },
      description: {
        text: {} as LocalizedString,
        highlightedText: [],
      },
      footnote: {
        en: "The links will also be duplicated in the mail.",
      } as LocalizedString,
      app: "{{appName}}",
      btn: {
        en: "Download",
      } as LocalizedString,
      steps: {
        first: {
          en: "Download {{appName}} app by using <b>the button at the bottom of the screen.</b>",
        } as LocalizedString,
        second: {
          en: "Once you have started the app, choose '{{singIn}}' (see the screenshot below).",
        } as LocalizedString,
        third: {
          en: "Enter your email and follow the instructions.",
        } as LocalizedString,
        fourth: {
          en: "After that is done, enjoy the premium features and start taking care of your loved ones!",
        } as LocalizedString,
      },
    },
  },
  paymentPopup: {
    total: {
      today: {
        en: "Total today",
      } as LocalizedString,
      day: {
        en: "Total <span>per day</span>",
      } as LocalizedString,
      week: {
        en: "Total <span>per week</span>",
      } as LocalizedString,
      total: {
        en: "Total",
      } as LocalizedString,
      totalItem: {
        en: "Item price",
      } as LocalizedString,
    },
    selectedTitles: {
      en: "You selected {{titleText}}",
    } as LocalizedString,
    trial: {
      en: "{{days}}-Day Trial",
    } as LocalizedString,
    editEmail: {
      title: {
        en: "Email",
      } as LocalizedString,
      placeholder: {
        en: "Enter your email",
      } as LocalizedString,
      doubleCheck: {
        en: "Please double-check your email address before proceeding further",
      } as LocalizedString,
      button: { en: "ok" } as LocalizedString,
    },
    or: {
      en: "or",
    } as LocalizedString,
    payWithCard: {
      en: "Pay with card",
    } as LocalizedString,
    cardElementRecurly: {
      cardNumber: {
        en: "Card number",
      } as LocalizedString,
      mm: {
        en: "MM",
      } as LocalizedString,
      yy: {
        en: "YY",
      } as LocalizedString,
      firstName: {
        en: "First Name",
      } as LocalizedString,
      lastName: {
        en: "Last Name",
      } as LocalizedString,
    },
    discountSave: { en: "SAVE {{discount}}%" } as LocalizedString,
    showPrivacy: false,
  },

  localizationEnabled: false,
  transitionVisibilityGender: "female-only",
  isShowedSkipButton: true,
  showGeneratedPhotoOnPaywall: true,
  upsaleOffer: {
    product: "",
    upsaleName: "",
    recurlyId: "",
    discount: "",
    textLineThrough: "",
    priceAmount: "",
  },
  showSpecialOffer: true,
  showFaceScan: true,
  fbPixelId: 1035517762982907,
  routeGroups: [],
  upsaleFirst: {},
  skipPosition: "left",
  upsaleWithoutTrial: {},
  showBeforeAfterOnPaywall: true,
  showPlanDescription: true,
  prelandText: {
    title: "Struggling to eat healthy or lose weight?",
    subTitle: "Get a custom meal plan tailored to your needs",
  },
  showImage: true,
  showMealPlanPaywall: true,
  showEmailMeals: false,
  showEmailReviews: false,
  showTwoPhones: false,
  addBlurDay1: false,
  dynamicPages: [],
  showEmailPopup: false,
  showGiftPopup: false,
  collectEmailAfterPayment: false,
  requestApplePayEmail: false,
  upsaleNutritionBundle: {
    product: "",
    upsaleName: "",
    recurlyId: "",
    priceAmount: "",
    movePriceToBottom: false,
  },
  dynamicRedirectRules: [],
  usersTrust: {
    title: {
      en: "1.2 Million Users Trust the Voery App",
    } as LocalizedString,
    reviewTitle: {
      en: "Loved by Travelers and Professionals",
    } as LocalizedString,
    reviewDescription: {
      en: "“I used AI Call to speak with an airline in Japan — it handled everything perfectly!”",
    } as LocalizedString,
  },
  stats: {
    image: "face-scan",
    firstOption: {
      en: "**1,300+** people took the quiz today",
    } as LocalizedString,
    secondOption: {
      en: "Trusted by **1.8M+** users worldwide",
    } as LocalizedString,
  },
  guide: {
    title: {
      en: "Ethnicity & Nutrition Report",
    } as LocalizedString,
    text: {
      en: "Discover how your genetics shape your nutrition and get clear, science-backed insights made easy to understand. Your report helps you finally see the patterns behind your wellness, metabolism, and daily performance.",
    } as LocalizedString,
    blurText: {
      en: "Unlock full access to read your complete report",
    } as LocalizedString,
  },
  tryFoodzee: {
    tryFoodzeeTitle: {
      en: "Your **Personalized Plan** Includes",
    } as LocalizedString,
    options: {
      en: [
        "Daily meal plan tailored to your goals",
        "AI Nutritionist available 24/7",
        "Grocery list & easy recipes",
        "Progress tracking & calorie insights",
      ],
    } as LocalizedArray,
    discount: {
      title: {
        en: "Personal discount applied",
      } as LocalizedString,
      subTitle: {
        en: "You save 95%",
      } as LocalizedString,
    },
  },
  features: {
    title: {
      en: "How Voery Works",
    } as LocalizedString,
    subTitle: {
      en: "Powerful AI that translates real\nconversations — calls and\nface-to-face — instantly and naturally.",
    } as LocalizedString,
    options: [
      {
        title: {
          en: "Real-Time Call Translation",
        } as LocalizedString,
        subTitle: {
          en: "Speak freely during online calls — Voery translates both sides in real time with voice and text.",
        } as LocalizedString,
      },
      {
        title: {
          en: "Voice-to-Voice Translation",
        } as LocalizedString,
        subTitle: {
          en: "Hear translated speech instantly in your language — no pauses, no awkward delays.",
        } as LocalizedString,
      },
      {
        title: {
          en: "Live Subtitles",
        } as LocalizedString,
        subTitle: {
          en: "See what the other person says in real time — perfectly translated on your screen.",
        } as LocalizedString,
      },
      {
        title: {
          en: "Offline Conversation Mode",
        } as LocalizedString,
        subTitle: {
          en: "Talk to people nearby — Voery listens, translates, and speaks for both of you automatically.",
        } as LocalizedString,
      },
      {
        title: {
          en: "Natural AI Voice",
        } as LocalizedString,
        subTitle: {
          en: "Clear, human-like speech that feels like a real conversation, not a robot.",
        } as LocalizedString,
      },
      {
        title: {
          en: "Simple Link to Start a Call",
        } as LocalizedString,
        subTitle: {
          en: "No downloads for your partner — just send a link and start talking.",
        } as LocalizedString,
      },
    ],
  },
  customersReview: [
    {
      text: {
        en: "I used Voery while traveling and it completely changed how I talk to locals. No typing, no awkward pauses — just real conversation.",
      } as LocalizedString,
    },
    {
      text: {
        en: "Voery saved my business calls. I can speak my language, my clients hear theirs — and the conversation just flows like normal.",
      } as LocalizedString,
    },
    {
      text: {
        en: "My partner and I speak different languages. With Voery, it finally feels like we’re having real conversations not just translating words.",
      } as LocalizedString,
    },
    {
      text: {
        en: "Sending a link and instantly talking in different languages feels unreal. The voice translation is fast, natural, and surprisingly accurate.",
      } as LocalizedString,
    },
  ],
  questions: [
    {
      question: {
        en: "How fast is the translation?",
      } as LocalizedString,
      answer: {
        en: "Translation happens in real time, so conversations feel smooth and natural.",
      } as LocalizedString,
    },
    {
      question: {
        en: "Does the other person need the app?",
      } as LocalizedString,
      answer: {
        en: "No. You can simply share a call link — the other person joins instantly.",
      } as LocalizedString,
    },
    {
      question: {
        en: "Can I use Voery for face-to-face conversations?",
      } as LocalizedString,
      answer: {
        en: "Yes. Voery can translate live speech between two people who are nearby.",
      } as LocalizedString,
    },
    {
      question: {
        en: "Which languages are supported?",
      } as LocalizedString,
      answer: {
        en: "Voery supports 35+ languages, including English, Spanish, French, German, Chinese, Japanese, Korean, and more.",
      } as LocalizedString,
    },
    {
      question: {
        en: "How does the trial work?",
      } as LocalizedString,
      answer: {
        en: "You start with a discounted paid trial and get full access to all features. If Voery isn’t right for you, you can cancel before the trial ends.",
      } as LocalizedString,
    },
    {
      question: {
        en: "How can I cancel my subscription?",
      } as LocalizedString,
      answer: {
        en: "You can cancel anytime in your personal account or by contacting our support team — we’re always happy to help.",
      } as LocalizedString,
    },
  ],
  showPlans: false,
  ltsPhotoExample: {
    title: {
      en: "Upload a selfie and get report with 97% accuracy",
    } as LocalizedString,
    photoProgressTitle: {
      en: "Analyzing your ethnicity - ",
    } as LocalizedString,
  },
  showPhotoProgress: false,
  email: {
    title: {
      en: "Ready to start?",
      es: "¿Listo para empezar?",
    } as LocalizedString,
    subTitle: {
      en: "Enter your email to activate your personal AI translator. Secure access. No spam.",
      es: "Ingresa tu email para activar tu traductor personal con IA. \nAcceso seguro. Sin spam.",
    } as LocalizedString,
  },
  upsaleDynamic: {
    product: "",
    upsaleName: "",
    recurlyId: "",
    priceAmount: "",
    buttonText: {
      en: "",
    } as LocalizedString,
    title: {
      en: "Mega Credits & Minutes Pack",
      cs: "Mega minuty a balíček kreditů",
      de: "Mega-Minuten & Credits-Paket",
      es: "Mega Minutos y Paquete de Créditos",
      fr: "Méga Minutes et Pack de Crédits",
      id: "Paket Mega Menit & Kredit",
      it: "Mega Minuti e Pacchetto Crediti",
      pt: "Mega Minutos e Pacote de Créditos",
      tr: "Mega Dakikalar ve Kredi Paketi",
    } as LocalizedString,
    subTitle: {
      en: "Limited-time offer (only now)",
      cs: "Upgrade na omezenou dobu (jen nyní)",
      de: "Zeitlich begrenztes Upgrade (nur jetzt)",
      es: "Actualización por tiempo limitado (solo ahora)",
      fr: "Mise à niveau limitée dans le temps (seulement maintenant)",
      id: "Peningkatan waktu terbatas (hanya sekarang)",
      it: "Aggiornamento a tempo limitato (solo ora)",
      pt: "Atualização por tempo limitado (apenas agora)",
      tr: "Sınırlı süreli yükseltme (yalnızca şimdi)",
    } as LocalizedString,
    image: "",
    descriptionTitle: {
      en: "",
    } as LocalizedString,
    descriptionText: {
      en: "Get biggest minutes bundle at the lowest price ever.",
      cs: "Získejte největší balíček minut za nejnižší cenu vůbec.",
      de: "Holen Sie sich Ihr größtes Minutenpaket zum bisher niedrigsten Preis.",
      es: "Obtén tu mayor paquete de minutos al precio más bajo de siempre.",
      fr: "Obtenez votre plus grand forfait de minutes au prix le plus bas jamais vu.",
      id: "Dapatkan bundel menit terbesar Anda dengan harga terendah yang pernah ada.",
      it: "Ottieni il tuo pacchetto di minuti più grande al prezzo più basso di sempre.",
      pt: "Obtenha seu maior pacote de minutos pelo menor preço de todos os tempos.",
      tr: "En büyük dakika paketinizi şimdiye kadarki en düşük fiyata alın.",
    } as LocalizedString,
    whatYouGetText: {
      en: ["2000 Minutes", "10 000 AI Credits", "Priority processing speed"],
      cs: [
        "3 000 minut",
        "1 500 AI kreditů",
        "Prioritní rychlost zpracování",
        "Bonus: +300 minut navíc",
      ],
      de: [
        "3.000 Minuten",
        "1.500 KI-Credits",
        "Prioritäre Verarbeitungsgeschwindigkeit",
        "Bonus: +300 Minuten extra",
      ],
      es: [
        "3.000 Minutos",
        "1.500 Créditos de IA",
        "Velocidad de procesamiento prioritaria",
        "Bonus: +300 Minutos extra",
      ],
      fr: [
        "3 000 Minutes",
        "1 500 Crédits IA",
        "Vitesse de traitement prioritaire",
        "Bonus: +300 Minutes supplémentaires",
      ],
      id: [
        "3.000 Menit",
        "1.500 Kredit AI",
        "Kecepatan pemrosesan prioritas",
        "Bonus: +300 Menit ekstra",
      ],
      it: [
        "3.000 Minuti",
        "1.500 Crediti IA",
        "Velocità di elaborazione prioritaria",
        "Bonus: +300 Minuti extra",
      ],
      pt: [
        "3.000 Minutos",
        "1.500 Créditos de IA",
        "Velocidade de processamento prioritária",
        "Bônus: +300 Minutos extras",
      ],
      tr: [
        "3.000 Dakika",
        "1.500 AI Kredisi",
        "Öncelikli işlem hızı",
        "Bonus: +300 Ekstra Dakika",
      ],
    } as LocalizedArray,
    bottomText: {
      en: "",
    } as LocalizedString,
    movePriceToBottom: false,
    letterName: "",
    fileName: "",
    // продукт приходит из мутатора — id и цены в дефолтах не держим
    products: { product: [] },
    timerLabel: {
      en: "Your discount reserved for 5 minutes only",
      cs: "Vaša sleva je rezervována na 5 minut",
      de: "Ihr Rabatt ist für 5 Minuten reserviert",
      es: "Tu descuento reservado por 5 min",
      fr: "Votre réduction réservée pour 5 min",
      id: "Diskon Anda dicadangkan selama 5 menit",
      it: "Il tuo sconto è riservato per 5 min",
      pt: "Seu desconto reservado por 5 min",
      tr: "İndiriminiz 5 dakika için rezerve edildi",
    } as LocalizedString,
    skipText: {
      en: "Skip for now",
      cs: "Přeskočit",
      de: "Jetzt überspringen",
      es: "Omitir por ahora",
      fr: "Passer pour l'instant",
      id: "Lewati untuk sekarang",
      it: "Salta per ora",
      pt: "Pular por agora",
      tr: "Şimdi atla",
    } as LocalizedString,
    priceNote: {
      en: "One-time payment. No renewals.",
      cs: "Jednorázová platba. Bez obnovení.",
      de: "Einmalige Zahlung. Keine Verlängerungen.",
      es: "Pago único. Sin renovaciones.",
      fr: "Paiement unique. Pas de renouvellements.",
      id: "Pembayaran sekali. Tanpa perpanjangan.",
      it: "Pagamento unico. Nessun rinnovo.",
      pt: "Pagamento único. Sem renovações.",
      tr: "Tek seferlik ödeme. Yenileme yok.",
    } as LocalizedString,
  },
  timerDesign: {
    text: {
      en: "Your Report expires in",
      cs: "Platnost vašeho reportu vyprší za",
      de: "Dein Bericht läuft ab in",
      es: "Tu informe caduca en",
      fr: "Votre rapport expire dans",
      id: "Laporan Anda kedaluwarsa dalam",
      it: "Il tuo report scade tra",
      pt: "Seu relatório expira em",
      tr: "Raporunuzun süresi doluyor:",
    } as LocalizedString,
    buttonTitle: {
      en: "Get Report",
      cs: "Získat report",
      de: "Bericht abrufen",
      es: "Obtener informe",
      fr: "Obtenir le rapport",
      id: "Dapatkan laporan",
      it: "Ottieni il report",
      pt: "Obter relatório",
      tr: "Raporu al",
    } as LocalizedString,
  },
  personalPlanPage: {
    title: {
      en: "Talk to anyone\nIn any language\nInstantly",
    } as LocalizedString,
    subTitle: {
      en: "Real-time translated calls and instant voice-to-voice translation for global communication.",
    } as LocalizedString,
    buttonTitle: {
      en: "Try now",
    } as LocalizedString,
    paymentButtonTitle: {
      en: "Try now",
    } as LocalizedString,
    // Блок money-back: каждое поле локализовано и показывается только если
    // непустое. Пустой title → не показываем иконку и заголовок.
    moneyBack: {
      title: {
        en: "",
      } as LocalizedString,
      sub1: {
        en: "At FoodZee we carefully designed our product and strongly believe in its quality. If you don't notice any progress after using the app for at least a week, we are ready to make a complete refund within 21 days.",
        cs: "Ve FoodZee jsme náš produkt pečlivě navrhli a věříme v jeho kvalitu. Pokud po týdnu používání nevidíte žádný pokrok, jsme připraveni vrátit peníze do 21 dnů.",
        de: "Bei FoodZee haben wir unser Produkt sorgfältig gestaltet und glauben fest an seine Qualität. Wenn Sie nach mindestens einer Woche Nutzung keine Fortschritte bemerken, erstatten wir Ihnen innerhalb von 21 Tagen den vollen Betrag zurück.",
        es: "En FoodZee diseñamos cuidadosamente nuestro producto y creemos firmemente en su calidad. Si no notas progreso después de usar la app por al menos una semana, te hacemos un reembolso completo en 21 días.",
        fr: "Chez FoodZee, nous avons conçu notre produit avec soin et croyons en sa qualité. Si vous ne constatez aucun progrès après au moins une semaine d’utilisation, nous sommes prêts à vous rembourser intégralement dans un délai de 21 jours.",
        id: "Di FoodZee kami merancang produk kami dengan hati-hati dan sangat percaya pada kualitasnya. Jika Anda tidak melihat kemajuan apa pun setelah menggunakan aplikasi setidaknya selama seminggu, kami siap memberikan pengembalian dana penuh dalam waktu 21 hari.",
        it: "Da FoodZee abbiamo progettato con cura il nostro prodotto e crediamo fermamente nella sua qualità. Se non noti miglioramenti dopo almeno una settimana di utilizzo, siamo pronti a rimborsarti completamente entro 21 giorni.",
        pt: "No FoodZee, desenhamos cuidadosamente nosso produto e acreditamos fortemente em sua qualidade. Se você não notar progresso após usar o app por pelo menos uma semana, faremos um reembolso completo dentro de 21 dias.",
        tr: "FoodZee olarak ürünümüzü özenle tasarladık ve kalitesine inanıyoruz. Uygulamayı en az bir hafta kullandıktan sonra ilerleme görmezseniz, 21 gün içinde tam iade yapmaya hazırız.",
      } as LocalizedString,
      sub2: {
        en: "Find more about the applicable limitations in our Terms of Use.",
        cs: "Více o omezeních naleznete v Podmínkách užití.",
        de: "Weitere Informationen zu den geltenden Einschränkungen finden Sie in unseren Nutzungsbedingungen.",
        es: "Consulta las limitaciones aplicables en nuestros Términos de uso.",
        fr: "Consultez nos Conditions d’utilisation pour plus d’informations sur les limitations applicables.",
        id: "Pelajari lebih lanjut tentang batasan yang berlaku dalam Ketentuan Penggunaan kami.",
        it: "Trova maggiori dettagli sulle limitazioni applicabili nei nostri Termini di Utilizzo.",
        pt: "Saiba mais sobre as limitações aplicáveis em nossos Termos de Uso.",
        tr: "Uygulanabilir sınırlamalar hakkında daha fazla bilgi için Kullanım Şartlarımızı inceleyin.",
      } as LocalizedString,
    },
    // Тайтл блока meal-plan:
    //   "" (пусто)  → динамический тайтл по весу (как сейчас)
    //   "none"      → тайтл не показывается
    //   любой текст → свой тайтл (на нужной локали)
    mealPlanTitle: {
      en: "",
    } as LocalizedString,
    sections: [
      "plans",
      "users-trust",
      "app-store-app",
      "features",
      "customers-review",
      "questions",
      "voery-call",
    ],
  },
  giftPopup: {
    title: {
      en: "You’re getting an exclusive **95% discount**",
    } as LocalizedString,
    buttonTitle: {
      en: "Continue",
    } as LocalizedString,
  },
  scrollEnabled: false,
  sendEthnicity: "none",
  showUpsalePaymentError: true,
  redirectToPwa: false,
  newPaymentModal: false,
  showEmailAutocomplete: false,
  activeLanguages: ["en"],
  showGetSelfieReportPopup: false,
  darkBlurEnabled: false,
  showApplePayAnimation: false,
  // Локали, кроме en, здесь не дублируем: незаполненный перевод откатывается
  // на en через getLocalizedText, а фейковый плейсхолдер юзер увидел бы как текст.
  paywall: {
    title: {
      en: "Speak any language.\nBe understood.",
    } as LocalizedString,
    optionsTitle: {
      en: "Use Voery to translate:",
    } as LocalizedString,
    buttonTitle: {
      en: "Start Trial",
    } as LocalizedString,
    paymentButtonTitle: {
      en: "Start Trial",
    } as LocalizedString,
    options: [
      { en: "Voice calls" } as LocalizedString,
      { en: "Live conversations" } as LocalizedString,
      { en: "Online meetings" } as LocalizedString,
      { en: "Travel & everyday talks" } as LocalizedString,
    ],
    // пусто → блок «no payment» на пейволле не показывается
    noPayment: {
      en: "",
    } as LocalizedString,
    bottomText: {
      en: "By continuing, you are starting a subscription that will automatically renew at the end of the selected period unless canceled at least 24 hours before the end of the current period. To avoid being charged, please cancel your subscription at least 24 hours before the period ends. If you’re unsure how to cancel, please refer to our Terms of Use. You may save a screenshot of this information for your records. By continuing, you also agree to our",
    } as LocalizedString,
    product: {},
  },
  changePaymentMethod: {
    title: {
      en: "Buy Credits",
      cs: "Mega minuty a balíček kreditů",
      de: "Mega-Minuten & Credits-Paket",
      es: "Mega Minutos y Paquete de Créditos",
      fr: "Méga Minutes et Pack de Crédits",
      id: "Paket Mega Menit & Kredit",
      it: "Mega Minuti e Pacchetto Crediti",
      pt: "Mega Minutos e Pacote de Créditos",
      tr: "Mega Dakikalar ve Kredi Paketi",
    } as LocalizedString,
    // пакеты кредитов приходят из мутатора — id и цены в дефолтах не держим
    products: [],
  }
};
